import SEO from '@/components/SEO';
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from '../../contexts/QuoteDrawerContext';
import { Palette, Eye, MessageCircle, Award, ArrowRight, Star, Zap, ChevronLeft, ChevronRight, PenTool, TrendingUp, Monitor, BarChart } from "lucide-react";
import { useState } from "react";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function Branding() {
  const { openDrawer } = useQuoteDrawer();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const services = [
    {
      title: "Brand Identity Design",
      description: "Complete visual identity including logo, colors, typography, and brand guidelines.",
      icon: <Palette className="h-8 w-8" />,
      color: "from-purple-500 to-blue-500"
    },
    {
      title: "Brand Strategy",
      description: "Strategic positioning and messaging that resonates with your target audience.",
      icon: <MessageCircle className="h-8 w-8" />,
      color: "from-pink-500 to-rose-500"
    },
    {
      title: "Visual Brand Assets",
      description: "Marketing materials, business cards, brochures, and digital assets.",
      icon: <Eye className="h-8 w-8" />,
      color: "from-amber-500 to-orange-500"
    },
    {
      title: "Brand Guidelines",
      description: "Comprehensive brand guidelines to ensure consistent brand application.",
      icon: <Award className="h-8 w-8" />,
      color: "from-emerald-500 to-teal-500"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      company: "TechNova",
      quote: "ZeeXSoft transformed our brand identity completely. The new design perfectly captures our company's vision.",
      rating: 5
    },
    {
      name: "Michael Chen",
      company: "GreenLife",
      quote: "The brand strategy session was eye-opening. We now have a clear direction for our messaging and visuals.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      company: "StyleHaven",
      quote: "Exceptional attention to detail in every asset they created. Our brand has never been stronger.",
      rating: 5
    }
  ];

  return (
    <>
      <SEO
        title="Professional Branding Services UAE | ZeeXSoft"
        description="Complete branding solutions in UAE including logo design, visual identity, and brand strategy since 2017."
        keywords="branding services uae, logo design sharjah, corporate identity, brand strategy dubai"
      />

      <main className="bg-gray-100 text-gray-900">
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Build a <span className="text-yellow-600">Powerful Brand Identity</span>
            </h1>
            <p className="text-xl max-w-3xl mx-auto mb-8">
              Comprehensive branding solutions to make your business stand out
            </p>
          </div>
        </section>



        {/* Services Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our Branding Services</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive solutions to build and elevate your brand
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Logo Design",
                  description: "Memorable logos that capture your brand essence",
                  icon: <PenTool className="h-8 w-8 text-yellow-400" />
                },
                {
                  title: "Visual Identity",
                  description: "Cohesive visual systems across all touchpoints",
                  icon: <Palette className="h-8 w-8 text-yellow-400" />
                },
                {
                  title: "Brand Strategy",
                  description: "Data-driven positioning and messaging",
                  icon: <TrendingUp className="h-8 w-8 text-yellow-400" />
                }
              ].map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  whileHover={{ y: -5 }}
                  className="bg-gray-100 rounded-xl p-8 hover:shadow-lg hover:shadow-yellow-500/10 transition-all"
                >
                  <div className="w-16 h-16 rounded-full bg-yellow-500/10 flex items-center justify-center mb-6 mx-auto">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{service.title}</h3>
                  <p className="text-gray-600 text-center">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Process */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-900/20 text-blue-300 mb-4">
                <Zap className="w-4 h-4 mr-2" />
                Our Process
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Strategic Brand Development</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                A proven methodology that transforms ideas into powerful brand experiences
              </p>
            </motion.div>

            <div className="relative">
              {/* Timeline line */}
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/20 via-blue-500 to-blue-500/20 -translate-x-1/2"></div>
              
              {[
                { 
                  step: "01", 
                  title: "Discovery & Research", 
                  description: "Deep dive into your business, market, and audience to uncover unique insights" 
                },
                { 
                  step: "02", 
                  title: "Strategy & Positioning", 
                  description: "Develop a compelling brand strategy and positioning framework" 
                },
                { 
                  step: "03", 
                  title: "Identity Design", 
                  description: "Create a distinctive visual identity that brings your brand to life" 
                },
                { 
                  step: "04", 
                  title: "Implementation", 
                  description: "Deploy your brand across all touchpoints with comprehensive guidelines" 
                }
              ].map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative mb-12 md:mb-24 ${index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2'}`}
                >
                  <div className="bg-gray-100 p-8 rounded-2xl shadow-sm border border-gray-200 relative">
                    <div className="absolute -left-4 top-8 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm z-10">
                      {item.step}
                    </div>
                    <div className="flex items-start">
                      <div className="ml-6 md:ml-8">
                        <h3 className="font-orbitron font-semibold text-xl text-gray-900 mb-3">
                          {item.title}
                        </h3>
                        <p className="text-gray-600">
                          {item.description}
                        </p>
                        <div className="mt-4 inline-flex items-center text-blue-500 font-medium group">
                          <span>Learn more</span>
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl mb-6">
                What Our Clients Say
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Hear from businesses that transformed their brand with us
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto">
              <AnimatePresence mode="wait">
                {testimonials.map((testimonial, index) => (
                  index === activeTestimonial && (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -50 }}
                      transition={{ duration: 0.5 }}
                      className="bg-gray-100 p-8 rounded-2xl shadow-sm border border-gray-200"
                    >
                      <div className="flex mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} 
                          />
                        ))}
                      </div>
                      <p className="text-lg italic text-gray-600 mb-6">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                          <p className="text-sm text-gray-600">{testimonial.company}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                            className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                          >
                            <ChevronLeft className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                            className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                          >
                            <ChevronRight className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="Branding" 
              services={[
                {
                  title: "Web Design & Development",
                  description: "Create a website that perfectly represents your brand identity.",
                  icon: <Monitor className="h-8 w-8" />
                },
                {
                  title: "Digital Marketing",
                  description: "Promote your brand across digital channels to reach your audience.",
                  icon: <BarChart className="h-8 w-8" />
                },
                {
                  title: "UI/UX Design",
                  description: "Design intuitive user experiences that reflect your brand values.",
                  icon: <PenTool className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="font-orbitron font-bold text-4xl mb-6 text-gray-900">
                Ready to Transform Your Brand?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Let's create a brand identity that stands out and drives results for your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={(e) => {
                    e.preventDefault();
                    openDrawer('Branding');
                  }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg font-medium rounded-xl hover:shadow-lg transition-all duration-300"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link href="/contact">
                  <Button 
                    variant="outline" 
                    className="bg-transparent border-2 border-gray-200 hover:bg-gray-100 text-gray-900 hover:text-gray-900 px-8 py-6 text-lg font-medium rounded-xl transition-all duration-300"
                  >
                    Contact Our Team
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
